import { usePermissionStore } from "@/stores/permission";
import { useMutation, useQuery, useQueryCache } from "@pinia/colada";
import {
	CreateReport,
	Report,
	UpdateReport,
	useApi,
	UserScope,
} from "@soft-db/monitoring-api-client";
import { composeCacheEffects, useQueryEnabled, useScopeDataUpdater, useScopedQuery } from "./query";

export const reportsKeys = {
	root: ["reports"] as const,

	list: () => [...reportsKeys.root, "list"] as const,

	byDist: (dist: string) => [...reportsKeys.list(), "dist", dist] as const,
	bySpace: (space: string) => [...reportsKeys.list(), "space", space] as const,

	byId: (id: string | null) => [...reportsKeys.root, "id", id] as const,
};

function useUpdateCacheById() {
	const update = useScopeDataUpdater();

	return (report: Report) => {
		update(reportsKeys.byId(report.id), report);
	};
}

// Use root key to update subscription toggle button
function useInvalidateListCache() {
	const cache = useQueryCache();

	return () => {
		cache.invalidateQueries({ key: reportsKeys.root });
	};
}

/**
 * Compose both updateCacheById and invalidateListCache effects
 */
function useReportCacheEffect() {
	return composeCacheEffects(useUpdateCacheById(), useInvalidateListCache());
}

function useReportEnabled(...args: MaybeRefOrGetter<string | boolean | null>[]) {
	const { hasPermission } = usePermissionStore();

	return useQueryEnabled(...args, () => hasPermission("report.get"));
}

export function useReport(id: MaybeRefOrGetter<string | null>) {
	const api = useApi().report();

	const { data: report, ...rest } = useScopedQuery({
		key: () => reportsKeys.byId(toValue(id)),
		query: () => api.fetch(toValue(id)!),
		enabled: useReportEnabled(id),
	});

	return {
		report,
		data: report,
		...rest,
	};
}

export function useScopeReports(scopeRef: MaybeRefOrGetter<UserScope>) {
	const api = useApi().report();

	const { data: reports, ...rest } = useQuery({
		key: () => {
			const scope = toValue(scopeRef);

			switch (scope.type) {
				case "distributor":
					return reportsKeys.byDist(scope.id);
				case "space":
					return reportsKeys.bySpace(scope.id);
				case "none":
					return ["/dev/null"];
			}
		},
		query: async () => {
			const scope = toValue(scopeRef);
			switch (scope.type) {
				case "distributor":
					return api.fetchDist(scope.id);
				case "space":
					return api.fetchSpace(scope.id);
				case "none":
					return [];
			}
		},
		enabled: useQueryEnabled(() => !!toValue(scopeRef)),
	});

	return { reports, data: reports, ...rest };
}

export function useCreateReport() {
	const api = useApi().report();

	return useMutation({
		mutation: ({ scope, data }: { scope: UserScope; data: CreateReport }) => {
			if (scope.type === "distributor") {
				return api.createWithDist(scope.id, data);
			} else if (scope.type === "space") {
				return api.createWithSpace(scope.id, data);
			}

			throw new Error("invalid scope for report creation");
		},
		onSuccess: useReportCacheEffect(),
	});
}

export function useUpdateReport() {
	const api = useApi().report();

	return useMutation({
		mutation: ({ id, data }: { id: string; data: UpdateReport }) => api.update(id, data),
		onSuccess: useReportCacheEffect(),
	});
}

export function useDeleteReport() {
	const api = useApi().report();

	return useMutation({
		mutation: ({ id }: { id: string }) => api.delete(id),
		onSuccess: useInvalidateListCache(),
	});
}
